import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, MessageSquare, Star, TrendingUp } from 'lucide-react';
import { Button } from "@/components/ui/button";
import VideoModal from "@/components/VideoModal";

const restaurantTestimonials = [
  {
    name: "Marco Rossi",
    role: "Owner, Bella Vista Italian",
    quote: "Our average order value increased by 22% in just 90 days. The AI recommendations are spot-on!",
    metric: "+22% Revenue",
    rating: 5
  },
  {
    name: "Lisa Park",
    role: "Manager, Seoul Kitchen",
    quote: "Table turnover improved by 25%. Customers order faster with AI suggestions. Game changer!",
    metric: "25% Faster Service",
    rating: 5
  },
  {
    name: "James Mitchell",
    role: "Chef-Owner, The Modern Plate",
    quote: "Finally, data-driven menu decisions! We removed 5 underperforming dishes and profits soared.",
    metric: "+18% Profit Margin",
    rating: 5
  }
];

const customerTestimonials = [
  {
    name: "Sarah Johnson",
    role: "Food Enthusiast",
    quote: "Found my new favorite restaurant! Asked for 'truffles but not too fancy' - perfect match!",
    metric: "5-star experience",
    rating: 5
  },
  {
    name: "Michael Chen",
    role: "Busy Professional",
    quote: "My allergies are always remembered. Safe dining without explaining every single time!",
    metric: "100% allergy safe",
    rating: 5
  },
  {
    name: "Elena Rodriguez",
    role: "Restaurant Explorer",
    quote: "AI learns my taste perfectly. Even suggests dishes I missed at my regular spots!",
    metric: "Perfect matches",
    rating: 5
  }
];

const Testimonials = () => {
  const [showRestaurants, setShowRestaurants] = useState(true);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const activeTestimonials = showRestaurants ? restaurantTestimonials : customerTestimonials;

  return (
    <section id="testimonials" className="py-12 bg-[#2256e5] relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
            <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              SUCCESS STORIES
            </span>
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">
            Real Results from <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">Real People</span>
          </h2>
          
          <p className="text-white/80 text-base max-w-2xl mx-auto">
            Hear from restaurants and diners who've transformed their experience with Dine AI
          </p>
        </motion.div>
        
        {/* Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-white/10 backdrop-blur-sm p-1 rounded-full inline-flex">
            <button
              onClick={() => setShowRestaurants(true)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                showRestaurants 
                  ? 'bg-white text-dineai-blue' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Restaurant Owners
            </button>
            <button
              onClick={() => setShowRestaurants(false)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                !showRestaurants 
                  ? 'bg-white text-dineai-blue' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Food Lovers
            </button>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <AnimatePresence mode="wait">
            {activeTestimonials.map((testimonial, index) => (
              <motion.div
                key={`${showRestaurants ? 'restaurant' : 'customer'}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/15 hover:scale-105 transition-all"
              >
                {/* Quote Icon */}
                <div className="mb-4">
                  <Quote className="w-8 h-8 text-dineai-yellow/30" />
                </div>
                
                {/* Testimonial Content */}
                <p className="text-white/90 text-base mb-4 italic">
                  "{testimonial.quote}"
                </p>
                
                {/* Metric Badge */}
                <div className="inline-flex items-center gap-2 bg-dineai-yellow/10 px-3 py-1 rounded-full mb-4">
                  {showRestaurants ? (
                    <TrendingUp className="w-4 h-4 text-dineai-yellow" />
                  ) : (
                    <Star className="w-4 h-4 text-dineai-yellow" />
                  )}
                  <span className="text-sm font-semibold text-dineai-yellow">
                    {testimonial.metric}
                  </span>
                </div>
                
                {/* Author Info */}
                <div className="border-t border-white/20 pt-4">
                  <p className="font-bold text-white">{testimonial.name}</p>
                  <p className="text-white/70 text-sm">{testimonial.role}</p>
                  
                  {/* Star Rating */}
                  <div className="flex mt-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < testimonial.rating
                            ? 'text-dineai-yellow fill-current'
                            : 'text-white/30'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* CTA Section */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="text-white/80 text-lg mb-6">
            Ready to join the fastest growing community of food lovers?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {showRestaurants ? (
              <>
                <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all">
                  SIGN UP NOW
                </Button>
                <Button
                  onClick={() => setIsVideoOpen(true)}
                  className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-medium rounded-full transform hover:scale-105 transition-all"
                >
                  WATCH DEMO
                </Button>
              </>
            ) : (
              <>
                <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all">
                  DOWNLOAD APP
                </Button>
                <Button className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-medium rounded-full transform hover:scale-105 transition-all">
                  VIEW RESTAURANTS
                </Button>
              </>
            )}
          </div>
        </motion.div>
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl="https://youtube.com/shorts/l3xW3Uv0iGM?feature=share"
        title="Dine AI Demo - Transform Your Restaurant"
        format="mobile"
      />
    </section>
  );
};

export default Testimonials;